/**
 * 12-Week Recruit Program
 * Beginner-friendly tactical fitness foundation
 * 3 days per week: Strength, Conditioning, Endurance
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'recruit-12week',
  name: '12-Week Recruit Foundation',
  description: 'Beginner tactical fitness program building fundamental strength and endurance',
  level: 'recruit',
  duration: 12,
  daysPerWeek: 3,
  goals: ['tactical-performance', 'strength', 'endurance', 'durability'],
  difficulty: 'beginner',
  version: '3.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false, // 3-day program; D4-D7 are rest — no range/enhancement content
};

// Recruit-specific periodization (simpler progression)
const periodization: ProgramPeriodizationModel = {
  style: 'linear',
  phases: [
    // Week 1, 6, 12: Testing
    {
      weeks: [1, 6, 12],
      type: 'assessment',
      name: 'Fitness Testing',
      focus: 'Establish baseline and track progress',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0
    },
    // Weeks 2-5: Foundation Building
    // Higher reps, moderate intensity — beginners build technique under
    // load before chasing weight. Intensity creeps from 65% (RPE ~6) to
    // 72% (RPE ~7) so users feel progression instead of plateauing.
    {
      weeks: [2, 3, 4, 5],
      type: 'accumulation',
      name: 'Foundation Phase',
      focus: 'Building movement quality and work capacity',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.65,
        endIntensity: 0.72,
        weeklyIncrease: 0.0175
      },
      trainingParameters: {
        repRange: [8, 12],
        setsMultiplier: 1.0,
        restPeriod: '60-90s'
      }
    },
    // Weeks 7-11: Progressive Overload
    // Lower reps, real strength loading. Intensity ramps from 72% to 82%
    // so by W11 the user is pulling 5-rep working sets at a real load —
    // the kind of stimulus they'll need to handle Grunt's 75-82% block.
    {
      weeks: [7, 8, 9, 10, 11],
      type: 'accumulation',
      name: 'Progressive Overload',
      focus: 'Increasing strength and endurance capacity',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.72,
        endIntensity: 0.82,
        weeklyIncrease: 0.02
      },
      trainingParameters: {
        repRange: [5, 8],
        setsMultiplier: 1.0,
        restPeriod: '90-120s'
      }
    }
  ]
};

// BASE TEMPLATES (periodization will transform these)

// Recruit testing week split across two days so a brand-new lifter
// doesn't try to test 8 different exercises in one 90-minute marathon.
// Strength tests on Mon, bodyweight + run + carry on Wed. Rest of week
// stays rest so beginners can absorb the demands and aren't sore for
// the W2 launch. Tue/Thu-Sun are rest.

const testingStrengthDay: WorkoutTemplateDay = {
  name: 'Strength Benchmarks',
  sessionType: 'assessment',
  estimatedDuration: 55,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep', details: 'Bike or brisk walk — be thorough, you\'re about to find your working loads' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for squat depth' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Posterior-chain activation' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Upper back on before pressing + rowing' },
        { name: 'Warm-Up Ramp Sets', format: 'prep', details: 'Build up in small jumps — 2-3 easy sets per lift before your test set. Never test cold.' },
      ],
    },
    {
      name: 'Program Maxes',
      type: 'testing',
      exercises: [
        { name: 'Trap Bar Deadlift', sets: 1, reps: '8-10RM', intensity: 'Max', details: 'Work up to best 8-10 rep load. Stop one rep before form breaks.', sessionType: 'assessment' },
        { name: 'Goblet Squat', sets: 1, reps: '10RM', intensity: 'Max', details: 'Controlled reps, full depth, dumbbell or kettlebell at chest', sessionType: 'assessment' },
        { name: 'Overhead Press (DB)', sets: 1, reps: '10RM', intensity: 'Max', details: 'Strict form, no leg drive', sessionType: 'assessment' },
        { name: 'Dumbbell Row', sets: 1, reps: '10RM', intensity: 'Max', details: 'One arm at a time. Strict, no body english.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '45s/side', format: 'recovery', details: 'Quads + hip flexors after squatting' },
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'After the deadlift' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const testingBodyweightCardioDay: WorkoutTemplateDay = {
  name: 'Bodyweight + Aerobic Benchmarks',
  sessionType: 'assessment',
  estimatedDuration: 45,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Run', duration: '5 min', format: 'cardio', intensity: 'Easy', details: 'Brisk walk-to-jog to raise heart rate' },
        { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Dynamic hips before the run' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Shoulder prep for the push-up test' },
        { name: 'Glute Bridge', sets: 1, reps: '15', format: 'prep', details: 'Glute activation before the run + carry' },
      ],
    },
    {
      name: 'Bodyweight Maxes',
      type: 'testing',
      exercises: [
        { name: 'Push-Ups', sets: 1, reps: 'AMRAP', format: 'bodyweight', details: 'Max strict reps in 2 minutes. Knees down OK if needed.', sessionType: 'assessment' },
        { name: 'Plank Hold', sets: 1, reps: 'Max Time', format: 'bodyweight', details: 'Stop when form breaks (hips sag).', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Aerobic + Work Capacity',
      type: 'testing',
      exercises: [
        { name: '1 Mile Run', duration: 'For Time', format: 'cardio', details: 'Record total time. Walk it if you need to.', sessionType: 'assessment' },
        { name: 'Farmer Carry', sets: 1, reps: '100m', format: 'carry', details: '½ bodyweight per hand. Stop and rest if needed.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee after the run' },
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after the push-up test' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

// STRENGTH DAY A. Paired with Strength B on Day 2 so a beginner gets TWO
// full-body resistance sessions a week. The program previously lifted only
// once a week, which is below the effective dose for a novice — 2-3x/week
// full-body is the standard beginner recommendation.
const strengthDay: WorkoutTemplateDay = {
  name: 'Full Body Strength A',
  sessionType: 'pt',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or brisk walk — raise core temp' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion so you can reach squat depth' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Wake the glutes before squatting + hinging' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep', details: 'Groove the hinge before the deadlift — this is the pattern beginners get wrong' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Upper back on before pressing + rowing' },
      ],
    },
    {
      name: 'Full Body',
      type: 'strength',
      exercises: [
        { name: 'Goblet Squat', sets: 3, reps: 10, intensity: 'progressive', rest: '90s', details: 'Focus on depth and control' },
        { name: 'Trap Bar Deadlift', sets: 3, reps: 8, intensity: 'progressive', rest: '2 min', details: 'Neutral spine — push the floor away' },
        { name: 'Overhead Press (DB)', sets: 3, reps: 10, intensity: 'progressive', rest: '90s', details: 'Strict, full ROM, no leg drive' },
        { name: 'Dumbbell Row', sets: 3, reps: 10, intensity: 'progressive', rest: '90s', details: 'One arm at a time, brace against bench. Squeezes the shoulder blade — fixes the postural slump from desk work.' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Dead Bug', sets: 3, reps: '10/side', intensity: 'progressive', rest: '45s', details: 'Anti-extension — keep the low back flat to the floor. Teaches bracing for the deadlift.' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '45s/side', format: 'recovery', details: 'Quads + hip flexors after squatting' },
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'After the hinging' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

// STRENGTH DAY B + CONDITIONING. Was a bare 30-min circuit with no warm-up,
// no cooldown, and no resistance work. Now it carries the SECOND weekly
// full-body lifting session — which is what lifts a beginner from 1x/week
// (below the effective dose) to 2x. It also adds the horizontal press the
// program was missing entirely despite TESTING max push-ups.
const conditioningDay: WorkoutTemplateDay = {
  name: 'Full Body Strength B + Conditioning',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    {
      // Was missing entirely — a beginner was starting burpees stone cold.
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Brisk walk or bike — build a light sweat' },
        { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Dynamic hips before the step-ups' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Glute activation' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Shoulder + serratus prep so the push-ups feel stable' },
      ],
    },
    {
      name: 'Full Body B',
      type: 'strength',
      exercises: [
        { name: 'Step-Ups', sets: 3, reps: 12, intensity: 'progressive', rest: '90s', details: 'Per leg — hold dumbbells once bodyweight is easy. Single-leg strength for rucking.' },
        { name: 'Push-Up', sets: 3, reps: 'AMRAP', intensity: 'bodyweight', rest: '90s', details: 'Chest to deck, body in one line. Knees down is fine — this is the movement your test measures.' },
        { name: 'Inverted Row', sets: 3, reps: 8, intensity: 'progressive', rest: '90s', details: 'Bar at hip height, body straight, pull chest to bar. More upright = easier; parallel to floor = harder.' },
      ],
    },
    {
      name: 'Conditioning Circuit',
      type: 'circuit',
      format: 'circuit',
      exercises: [
        { name: 'Burpees', sets: 3, reps: 10, format: 'circuit', details: 'Controlled tempo — step back instead of jumping if needed' },
        { name: 'Farmer Carry', sets: 3, reps: '50m', format: 'circuit', details: '½ bodyweight load if possible, tall posture' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Plank', sets: 3, reps: '30s', format: 'bodyweight', rest: '45s', details: 'Braced, hips level, no sag — the exact position your test scores' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after the push-ups' },
        { name: 'Quad Stretch', duration: '45s/side', format: 'recovery', details: 'After the step-ups' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const enduranceDay: WorkoutTemplateDay = {
  name: 'Endurance + Mobility',
  sessionType: 'pt',
  estimatedDuration: 40,
  blocks: [
    {
      name: 'Aerobic Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Easy walk to start — ease into it' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Ankles first — they take the load in walking under a pack' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Carry the load with the glutes, not the low back' },
      ],
    },
    {
      name: 'Aerobic Base',
      type: 'conditioning',
      exercises: [
        { name: 'Ruck/Walk', duration: '20-30 min', format: 'ruck', details: 'Steady conversational pace — you should be able to hold a sentence' },
      ],
    },
    {
      // Inverted Row MOVED to Day 2's strength block. It was a real working set
      // sitting inside a type:'recovery' block, so the session engine treated it
      // as mobility — never loaded, never progressed. Day 3 is now purely
      // aerobic + recovery, which is what it was always meant to be.
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee — the walk loads the calves most' },
        { name: 'Couch Stretch', duration: '45s/side', format: 'recovery', details: 'Hip flexors + quads after the miles' },
        { name: 'Lower Back Stretch', duration: '1 min', format: 'recovery', details: 'Child\'s pose / cat-cow — decompress the spine' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

export const recruitProgram12Week: WorkoutProgram = {
  metadata,
  periodization,
  templates: {
    // Testing weeks (W1, W6, W12) — strength Mon, bodyweight+aerobic
    // Wed. Two short fresh tests instead of one 8-exercise marathon.
    'testing-week-day1': testingStrengthDay,
    'testing-week-day2': restDay,
    'testing-week-day3': testingBodyweightCardioDay,
    'testing-week-day4': restDay,
    'testing-week-day5': restDay,
    'testing-week-day6': restDay,
    'testing-week-day7': restDay,

    // Training cycle (W2-5, W7-11)
    'training-day1': strengthDay,
    'training-day2': conditioningDay,
    'training-day3': enduranceDay,
    'training-day4': restDay,
    'training-day5': restDay,
    'training-day6': restDay,
    'training-day7': restDay,
  },
  workouts: [],
};

